import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { X } from '@b3networks/shared/common';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  ACTIONS_AUTHORIZE_ALL_RESOURCES,
  REGISTERED_ICONS,
  SERVICES_AUTHORIZE_ALL_RESOURCES
} from './iam-v2.constants';
import { IAMV2, IAMV2Resource, IAMV2ServiceName } from './iam-v2.modal';
import { IAMV2Store } from './iam-v2.store';

export interface AuditGroup {
  groupNames: string[];
}

@Injectable({
  providedIn: 'root'
})
export class IAMV2Service {
  private resourceCache = new Map<string, IAMV2Resource[]>();

  // URL mapping configuration
  private readonly urlMap: Record<string, (orgUuid: string) => string> = {
    [IAMV2ServiceName.call]: orgUuid => `/auth/private/v1/organizations/${orgUuid}/teams`,
    [IAMV2ServiceName.fax]: () => `/callcenter/private/v1/fax/resources`,
    [IAMV2ServiceName.sip]: () => `/callcenter/private/v1/sipTrunk/resources?service=sip`,
    [IAMV2ServiceName.byoc]: () => `/callcenter/private/v1/sipTrunk/resources?service=byoc`,
    [IAMV2ServiceName.byop]: () => `/callcenter/private/v1/sipTrunk/resources?service=byop`,
    [IAMV2ServiceName.hotDesking]: () => `/callcenter/private/v1/sipTrunk/resources?service=hotDesking`,
    [IAMV2ServiceName.autoAttendant]: () => `/workflow/private/v1/workflow/resources`,
    [IAMV2ServiceName.audit]: () => `/data/private/v1/auditGroup`,
    [IAMV2ServiceName.license]: orgUuid => `/auth/private/v1/organizations/${orgUuid}/teams?status=true`,
    [IAMV2ServiceName.team]: orgUuid => `/auth/private/v1/organizations/${orgUuid}/teams?status=true`,
    [IAMV2ServiceName.user]: orgUuid => `/auth/private/v1/organizations/${orgUuid}/teams?status=true`,
    [IAMV2ServiceName.developer]: () => `/flow/private/app/v1/iam/resources`
  };

  constructor(
    private http: HttpClient,
    private store: IAMV2Store
  ) {}

  getList(): Observable<IAMV2[]> {
    return this.http.get<IAMV2[]>(`/auth/private/v2/iam/list`).pipe(
      map(list => this.markResourceAvailability(list)),
      tap(list => {
        this.store.set(list);
      })
    );
  }

  private markResourceAvailability(list: IAMV2[]): IAMV2[] {
    return list.map(item => ({
      ...item,
      service: {
        ...item.service,
        authorizeAllResources: this.shouldAuthorizeAllResources(item.service.name),
        icon: item.service.name && REGISTERED_ICONS.has(item.service.name) ? item.service.name : 'default'
      },
      actions: item.actions?.map(action => ({
        ...action,
        authorizeAllResources: this.shouldAuthorizeAllResources(action.name, item.service.name)
      }))
    }));
  }

  private shouldAuthorizeAllResources(name: string, serviceName?: string): boolean {
    if (serviceName) {
      return ACTIONS_AUTHORIZE_ALL_RESOURCES.includes(name) || this.shouldAuthorizeAllResources(serviceName);
    }
    return SERVICES_AUTHORIZE_ALL_RESOURCES.includes(name) || !this.getUrl(name);
  }

  getResourcesByServiceName(name: string): Observable<IAMV2Resource[] | null> {
    const cached = this.resourceCache.get(name);
    if (cached) {
      return of(cached);
    }

    const url = this.getUrl(name);
    if (!url) {
      return of(null);
    }

    return this.http.get<IAMV2Resource[]>(url).pipe(
      map(resources => this.process(name, resources)),
      tap(resources => this.updateCache(name, resources))
    );
  }

  private process(name: string, resources: IAMV2Resource[] | AuditGroup): IAMV2Resource[] {
    let result = resources;

    if (name === IAMV2ServiceName.call) {
      result = (resources as IAMV2Resource[]).filter(resource => resource.active === true);
    }

    if (name === IAMV2ServiceName.audit) {
      return (resources as AuditGroup).groupNames.map(group => ({
        label: this.formatGroupName(group),
        resourceID: group
      }));
    }

    return (result as IAMV2Resource[]).map(resource => ({
      ...resource,
      resourceID: resource.resourceID || resource.uuid,
      label: resource.label || resource.name
    }));
  }

  private formatGroupName(group: string): string {
    return group
      ?.replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between camelCase words
      ?.replace(/_/g, ' ') // Replace underscores with spaces
      ?.replace(/\b\w/g, char => char.toUpperCase()); // Capitalize the first letter of each word
  }

  private updateCache(name: string, resources: IAMV2Resource[]): void {
    this.resourceCache.set(name, resources);
    this.store.update(state => ({
      resourceMap: new Map(state.resourceMap).set(name, resources)
    }));
  }

  private getUrl(name: string): string {
    const builder = this.urlMap[name];
    return builder ? builder(X.orgUuid) : '';
  }
}
